"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Phone, ShieldCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const keyPointers = [{
  title: "Family and pet-conscious solutions",
  description: "Biodegradable, low-toxicity applications targeted specifically to pest biology."
}, {
  title: "Exterior perimeter barrier defense",
  description: "Foundation, subfloor, and eave barriers designed to intercept pests before entry."
}, {
  title: "Auckland-wide local coverage",
  description: "Experienced technicians who understand North Shore, Central, and West Auckland climates."
}];
export default function Overview() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="overview" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Narrative, Pristine Datum & Action Column */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUpVariant} className="lg:col-span-6 flex flex-col items-start">
            {/* Pristine Datum: 2px fresh green accent bar */}
            <div className="w-9 h-[2px] bg-primary mb-5" />

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary border border-primary/20 text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4">
              <span>Residential Pest Control</span>
            </div>

            <h1 className="font-default text-3xl sm:text-4xl lg:text-[2.65rem] font-bold text-foreground leading-[1.18] tracking-tight mb-4">
              Safe, targeted pest management for your home
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground font-normal leading-relaxed mb-6">
              Stop rodents, insects, and wasps before they step inside with pet-conscious treatments tailored to Auckland weather.
            </p>

            <p className="text-sm sm:text-base text-foreground/80 leading-relaxed mb-8">
              Auckland's humid coastal climate creates constant pressure from ants, cockroaches, spiders, and seasonal rodents. Rather than flooding your living spaces with harsh chemicals, our certified specialists create a resilient exterior barrier along foundation lines, subfloors, and eaves. When interior treatment is necessary, we use precise baiting and family-safe formulations so your household stays protected and comfortable.
            </p>

            {/* Benefit Checkpoints */}
            <div className="w-full space-y-4 mb-8">
              {keyPointers.map((item, i) => <div key={item.title} data-index={i} className="flex items-start gap-3.5 text-left">
                  <div className="size-5 rounded-full bg-secondary text-primary flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 className="size-3.5 stroke-[2.5]" />
                  </div>
                  <div>
                    <h2 className="text-sm sm:text-base font-semibold text-foreground leading-snug">
                      {item.title}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-0.5">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 w-full sm:w-auto">
              <Button size="lg" className="font-semibold text-sm" onClick={() => {
              if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent('eco-open-quote-modal'));
              }
            }}>
                <span>Get a free quote</span>
                <ArrowRight className="size-4" />
              </Button>

              <Button asChild variant="secondary" size="lg" className="font-semibold text-sm border border-border hover:bg-accent hover:text-accent-foreground">
                <a href="tel:0221002103">
                  <Phone className="size-4 text-primary" />
                  <span>Call 022 100 2103</span>
                </a>
              </Button>
            </div>
          </motion.div>

          {/* Authentic Auckland Home Imagery Column */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUpVariant} className="lg:col-span-6 relative">
            <div className="relative rounded-xl overflow-hidden border border-border shadow-md bg-muted">
              <AspectRatio ratio={4 / 3}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/indoor_pest_control_misting_technician.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/indoor_pest_control_misting_technician.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/indoor_pest_control_misting_technician.png-wvc-sizes" alt="Certified pest control technician in full protective coveralls and respirator applying an indoor misting treatment inside an Auckland home" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </AspectRatio>

              {/* Top caption band */}
              <div className="absolute top-4 left-4 right-4 bg-foreground/60 backdrop-blur-sm rounded-lg px-4 py-2.5">
                <p className="text-lg sm:text-xl font-bold text-white tracking-tight">
                  Auckland Pest Control Services
                </p>
              </div>

              {/* Verified technician trust badge */}
              <div className="absolute bottom-4 left-4 right-4 sm:right-auto bg-background/95 backdrop-blur-sm border border-border rounded-lg p-3.5 shadow-sm flex items-center gap-3">
                <div className="size-10 rounded-md bg-secondary flex items-center justify-center text-primary shrink-0">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground tracking-tight">
                    Licensed & Certified Technicians
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Compliant with New Zealand safety standards
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}